import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, LogOut, CalendarPlus, Link2, Pencil, Download } from "lucide-react";
import partnerApi from "@/lib/partnerApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const EMPTY_CONTACT = { first_name: "", last_name: "", phone: "", email: "" };
const EMPTY_INS = { provider: "", member_id: "", group_number: "", plan: "" };
const EMPTY_PATIENT = { full_name: "", date_of_birth: "", vaccine: "", vaccines: [], insurance_coverage: "", insurances: [{ ...EMPTY_INS }] };
const EMPTY_DRIVE = { address: "", date: "", time: "", staff_count: "", residents_count: "", staff_flu: "", staff_covid: "", staff_rsv: "", residents_flu: "", residents_covid: "", residents_rsv: "", contacts: [{ ...EMPTY_CONTACT }], patients: [] };

function statusColor(s) {
  return s === "confirmed" ? "bg-primary" : s === "rescheduled" ? "bg-orange-500" : "bg-slate-400";
}

const VACC_LABEL = { flu: "Flu", covid: "COVID", rsv: "RSV", "": "—" };
const COV_LABEL = { covered: "Covered", not_covered: "Not covered", "": "—" };

function vaccNames(p) {
  const list = p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : []);
  return list.map((v) => VACC_LABEL[v] || v).join(", ") || "—";
}

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportRequest(r) {
  const lines = [];
  const kv = (k, v) => lines.push(`${csvCell(k)},${csvCell(v)}`);
  lines.push("Crystal Pharmacy — Confirmed Vaccine Drive");
  kv("Status", r.status);
  kv("Facility / Address", r.address);
  kv("Date", r.date);
  kv("Time", r.time);
  if (r.reschedule_note) kv("Pharmacy note", r.reschedule_note);
  lines.push("");
  lines.push("Group,Requested,Flu,COVID,RSV");
  lines.push(["Staff", r.staff_count || 0, r.staff_flu || 0, r.staff_covid || 0, r.staff_rsv || 0].map(csvCell).join(","));
  lines.push(["Residents", r.residents_count || 0, r.residents_flu || 0, r.residents_covid || 0, r.residents_rsv || 0].map(csvCell).join(","));
  if ((r.patients || []).length) {
    lines.push("");
    lines.push("Patient roster");
    lines.push("Full name,Date of birth,Vaccine,Insurance coverage,Insurance(s)");
    (r.patients || []).forEach((p) => {
      const ins = (p.insurances || []).map((x) => [x.provider, x.member_id, x.group_number, x.plan].filter(Boolean).join(" / ")).filter(Boolean).join("; ");
      lines.push([p.full_name, p.date_of_birth || "", vaccNames(p), COV_LABEL[p.insurance_coverage || ""] || p.insurance_coverage, ins].map(csvCell).join(","));
    });
  }
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safe = (r.address || "drive").replace(/[^a-z0-9]+/gi, "-").slice(0, 40).replace(/^-|-$/g, "");
  a.href = url; a.download = `vaccine-drive-${safe || "request"}-${r.date || ""}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  toast.success("Confirmed drive exported.");
}

function DriveForm({ initial, onSubmit, submitLabel, onCancel }) {
  const [d, setD] = useState(initial);
  const setF = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setContact = (i, k, v) => setD((x) => ({ ...x, contacts: x.contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const setPatient = (i, k, v) => setD((x) => ({ ...x, patients: x.patients.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));
  const toggleVaccine = (pi, key) => setD((x) => ({ ...x, patients: x.patients.map((p, idx) => {
    if (idx !== pi) return p;
    const cur = p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : []);
    const next = cur.includes(key) ? cur.filter((v) => v !== key) : [...cur, key];
    return { ...p, vaccines: next };
  }) }));
  const setIns = (pi, ii, k, v) => setD((x) => ({ ...x, patients: x.patients.map((p, idx) => idx === pi ? { ...p, insurances: p.insurances.map((ins, j) => (j === ii ? { ...ins, [k]: v } : ins)) } : p) }));

  function submit(e) {
    e.preventDefault();
    if (!d.address || !d.date || !d.time) return toast.error("Address, date and time are required.");
    if (d.contacts.some((c) => !c.first_name || !c.last_name || !c.email)) return toast.error("Each contact needs first name, last name and email.");
    const n = (v) => Number(v) || 0;
    const patients = (d.patients || []).map((p) => {
      const vaccines = p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : []);
      return { ...p, vaccines, vaccine: vaccines[0] || "" };
    });
    onSubmit({ ...d, patients, staff_count: n(d.staff_count), residents_count: n(d.residents_count),
      staff_flu: n(d.staff_flu), staff_covid: n(d.staff_covid), staff_rsv: n(d.staff_rsv),
      residents_flu: n(d.residents_flu), residents_covid: n(d.residents_covid), residents_rsv: n(d.residents_rsv) });
  }

  return (
    <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-5" data-testid="partner-drive-form">
      <div className="space-y-2"><Label>Address</Label><Input data-testid="pd-address" value={d.address} onChange={(e) => setF("address", e.target.value)} required /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Date</Label><Input type="date" data-testid="pd-date" value={d.date} onChange={(e) => setF("date", e.target.value)} required /></div>
        <div className="space-y-2"><Label>Time</Label><Input type="time" data-testid="pd-time" value={d.time} onChange={(e) => setF("time", e.target.value)} required /></div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Contact person(s)</Label>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setF("contacts", [...d.contacts, { ...EMPTY_CONTACT }])} data-testid="pd-add-contact"><Plus className="mr-1 h-4 w-4" /> Add a contact person</Button>
        </div>
        {d.contacts.map((c, i) => (
          <div key={i} className="rounded-xl border border-border/60 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold text-muted-foreground">Contact {i + 1}</span>{d.contacts.length > 1 && <button type="button" onClick={() => setF("contacts", d.contacts.filter((_, x) => x !== i))} className="text-destructive"><Trash2 className="h-4 w-4" /></button>}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="First name" value={c.first_name} onChange={(e) => setContact(i, "first_name", e.target.value)} required />
              <Input placeholder="Last name" value={c.last_name} onChange={(e) => setContact(i, "last_name", e.target.value)} required />
              <Input placeholder="Phone" value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} />
              <Input placeholder="Email" type="email" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} required />
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-border/60 p-4">
          <Label className="text-sm font-semibold">Staff</Label>
          <div className="space-y-1.5"><Label className="text-xs">How many requested a vaccination?</Label><Input type="number" min="0" data-testid="pd-staff" value={d.staff_count} onChange={(e) => setF("staff_count", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">Flu</Label><Input type="number" min="0" data-testid="pd-staff-flu" value={d.staff_flu} onChange={(e) => setF("staff_flu", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">COVID</Label><Input type="number" min="0" data-testid="pd-staff-covid" value={d.staff_covid} onChange={(e) => setF("staff_covid", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">RSV</Label><Input type="number" min="0" data-testid="pd-staff-rsv" value={d.staff_rsv} onChange={(e) => setF("staff_rsv", e.target.value)} /></div>
          </div>
        </div>
        <div className="space-y-3 rounded-xl border border-border/60 p-4">
          <Label className="text-sm font-semibold">Residents</Label>
          <div className="space-y-1.5"><Label className="text-xs">How many requested a vaccination?</Label><Input type="number" min="0" data-testid="pd-residents" value={d.residents_count} onChange={(e) => setF("residents_count", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">Flu</Label><Input type="number" min="0" data-testid="pd-residents-flu" value={d.residents_flu} onChange={(e) => setF("residents_flu", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">COVID</Label><Input type="number" min="0" data-testid="pd-residents-covid" value={d.residents_covid} onChange={(e) => setF("residents_covid", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">RSV</Label><Input type="number" min="0" data-testid="pd-residents-rsv" value={d.residents_rsv} onChange={(e) => setF("residents_rsv", e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div><Label>Patient roster</Label><p className="text-xs text-muted-foreground">Full name, date of birth and insurance for each person (optional).</p></div>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setF("patients", [...d.patients, { ...EMPTY_PATIENT, insurances: [{ ...EMPTY_INS }] }])} data-testid="pd-add-patient"><Plus className="mr-1 h-4 w-4" /> Add patient</Button>
        </div>
        {d.patients.map((p, pi) => (
          <div key={pi} className="rounded-xl border border-border/60 p-3" data-testid={`pd-patient-${pi}`}>
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold text-muted-foreground">Patient {pi + 1}</span><button type="button" onClick={() => setF("patients", d.patients.filter((_, x) => x !== pi))} className="text-destructive"><Trash2 className="h-4 w-4" /></button></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Full name" value={p.full_name} onChange={(e) => setPatient(pi, "full_name", e.target.value)} />
              <Input placeholder="Date of birth" type="date" value={p.date_of_birth} onChange={(e) => setPatient(pi, "date_of_birth", e.target.value)} />
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Vaccine(s)</Label>
                <div className="mt-1.5 flex flex-wrap gap-4">
                  {[["flu", "Flu"], ["covid", "COVID"], ["rsv", "RSV"]].map(([val, lbl]) => {
                    const sel = (p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : [])).includes(val);
                    return (
                      <label key={val} className="flex cursor-pointer items-center gap-1.5 text-sm">
                        <input type="checkbox" className="h-4 w-4 rounded border-input accent-primary" checked={sel} onChange={() => toggleVaccine(pi, val)} data-testid={`pd-patient-vaccine-${pi}-${val}`} />
                        {lbl}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Insurance coverage</Label>
                <select className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={p.insurance_coverage || ""} onChange={(e) => setPatient(pi, "insurance_coverage", e.target.value)} data-testid={`pd-patient-coverage-${pi}`}>
                  <option value="">Select coverage</option>
                  <option value="covered">Covered by insurance</option>
                  <option value="not_covered">Not covered by insurance</option>
                </select>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {p.insurances.map((ins, ii) => (
                <div key={ii} className="rounded-lg bg-muted/40 p-2">
                  <div className="mb-1 flex items-center justify-between"><span className="text-[11px] font-medium text-muted-foreground">Insurance {ii + 1}</span>{p.insurances.length > 1 && <button type="button" onClick={() => setPatient(pi, "insurances", p.insurances.filter((_, x) => x !== ii))} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input placeholder="Provider" value={ins.provider} onChange={(e) => setIns(pi, ii, "provider", e.target.value)} />
                    <Input placeholder="Member ID" value={ins.member_id} onChange={(e) => setIns(pi, ii, "member_id", e.target.value)} />
                    <Input placeholder="Group #" value={ins.group_number} onChange={(e) => setIns(pi, ii, "group_number", e.target.value)} />
                    <Input placeholder="Plan" value={ins.plan} onChange={(e) => setIns(pi, ii, "plan", e.target.value)} />
                  </div>
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => setPatient(pi, "insurances", [...p.insurances, { ...EMPTY_INS }])} data-testid={`pd-add-insurance-${pi}`}><Plus className="mr-1 h-3.5 w-3.5" /> Add additional insurance</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button type="submit" className="rounded-full" data-testid="pd-submit">{submitLabel}</Button>
        {onCancel && <Button type="button" variant="ghost" className="rounded-full" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  );
}

export default function HealthPartners() {
  const [params, setParams] = useSearchParams();
  const [partner, setPartner] = useState(null);
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState(null);
  const [link, setLink] = useState({ full_name: "", email: "" });

  const loadRequests = useCallback(async () => {
    try { const { data } = await partnerApi.get("/health-partners/vaccine-drive/requests"); setRequests(data); } catch { /* */ }
  }, []);

  const loadMe = useCallback(async () => {
    try { const { data } = await partnerApi.get("/health-partners/me"); setPartner(data); loadRequests(); }
    catch { localStorage.removeItem("hp_token"); setPartner(null); }
  }, [loadRequests]);

  useEffect(() => {
    const login = params.get("login");
    const token = params.get("token");
    if (login) {
      partnerApi.post("/health-partners/magic-login", { token: login })
        .then(({ data }) => { localStorage.setItem("hp_token", data.token); setPartner(data.partner); loadRequests(); toast.success("Signed in."); })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sign-in link invalid or expired."));
      params.delete("login"); setParams(params, { replace: true });
      return;
    }
    if (token) {
      partnerApi.post("/health-partners/claim-token", { token })
        .then(({ data }) => { setEmail(data.email); setCodeSent(true); setMode("login"); toast.success("Check your email for a login code."); })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid link."));
      params.delete("token"); setParams(params, { replace: true });
    }
    if (localStorage.getItem("hp_token")) loadMe();
  }, []); // eslint-disable-line

  async function sendCode(e) {
    e.preventDefault();
    if (!email) return toast.error("Enter your email.");
    setBusy(true);
    try {
      if (mode === "register") {
        if (!name) { setBusy(false); return toast.error("Enter your name."); }
        await partnerApi.post("/health-partners/register", { email, name });
      } else {
        await partnerApi.post("/health-partners/request-code", { email });
      }
      setCodeSent(true);
      toast.success("If an account exists, a login code was emailed to you.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  }

  async function verify(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/health-partners/verify-code", { email, code });
      localStorage.setItem("hp_token", data.token);
      setPartner(data.partner); setCode(""); setCodeSent(false);
      loadRequests();
      toast.success("Signed in.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code."); }
    finally { setBusy(false); }
  }

  function logout() { localStorage.removeItem("hp_token"); setPartner(null); setRequests([]); }

  async function createDrive(payload) {
    try { await partnerApi.post("/health-partners/vaccine-drive/requests", payload); toast.success("Vaccine drive request submitted."); setShowNew(false); loadRequests(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function updateDrive(id, payload) {
    try { await partnerApi.patch(`/health-partners/vaccine-drive/requests/${id}`, payload); toast.success("Request updated."); setEditId(null); loadRequests(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function linkExisting(e) {
    e.preventDefault();
    try { await partnerApi.post("/health-partners/vaccine-drive/link", link); toast.success("Request linked to your account."); setLink({ full_name: "", email: "" }); loadRequests(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No match found."); }
  }

  // ---------- Auth screen ----------
  if (!partner) {
    return (
      <div className="mx-auto max-w-md px-6 py-20" data-testid="hp-auth">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><ShieldCheck className="h-6 w-6" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold">Health Partner Portal</h1>
            <p className="text-sm text-muted-foreground">HIPAA-compliant access for facilities and partners.</p>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          {!codeSent ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div className="flex gap-2 rounded-full bg-muted p-1 text-sm">
                <button type="button" onClick={() => setMode("login")} data-testid="hp-tab-login" className={`flex-1 rounded-full py-1.5 font-medium ${mode === "login" ? "bg-white shadow" : "text-muted-foreground"}`}>Log in</button>
                <button type="button" onClick={() => setMode("register")} data-testid="hp-tab-register" className={`flex-1 rounded-full py-1.5 font-medium ${mode === "register" ? "bg-white shadow" : "text-muted-foreground"}`}>Create account</button>
              </div>
              {mode === "register" && <div className="space-y-2"><Label>Name / facility</Label><Input data-testid="hp-name" value={name} onChange={(e) => setName(e.target.value)} /></div>}
              <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="hp-email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="hp-send-code">{busy ? "Sending..." : mode === "register" ? "Create account & email code" : "Email me a login code"}</Button>
              <p className="text-center text-xs text-muted-foreground">We'll email you a 6-digit code to sign in — no password needed.</p>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter the 6-digit code we sent to <b>{email}</b>.</p>
              <Input data-testid="hp-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="text-center text-2xl tracking-[0.5em]" />
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="hp-verify">{busy ? "Verifying..." : "Verify & sign in"}</Button>
              <button type="button" onClick={() => { setCodeSent(false); setCode(""); }} className="w-full text-center text-xs text-muted-foreground hover:underline">Use a different email</button>
            </form>
          )}
        </div>
        <p className="mt-4 text-center text-sm"><Link to="/vaccine-drive" className="text-primary hover:underline">Request a vaccine drive without an account</Link></p>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="hp-dashboard">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Welcome, {partner.name}</h1>
          <p className="text-sm text-muted-foreground">{partner.email}</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={logout} data-testid="hp-logout"><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <Button className="rounded-full" onClick={() => { setShowNew((s) => !s); setEditId(null); }} data-testid="hp-new-drive"><CalendarPlus className="mr-1 h-4 w-4" /> New vaccine drive request</Button>
      </div>

      {showNew && <div className="mb-8"><DriveForm initial={{ ...EMPTY_DRIVE }} submitLabel="Submit request" onSubmit={createDrive} onCancel={() => setShowNew(false)} /></div>}

      <div className="mb-8 rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold"><Link2 className="h-5 w-5 text-primary" /> Link an existing request</h2>
        <p className="mb-3 text-xs text-muted-foreground">Already submitted a request? Link it by the exact full name and email used.</p>
        <form onSubmit={linkExisting} className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">Full name</Label><Input className="w-48" data-testid="hp-link-name" value={link.full_name} onChange={(e) => setLink({ ...link, full_name: e.target.value })} required /></div>
          <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" className="w-56" data-testid="hp-link-email" value={link.email} onChange={(e) => setLink({ ...link, email: e.target.value })} required /></div>
          <Button type="submit" variant="outline" className="rounded-full" data-testid="hp-link-submit">Link request</Button>
        </form>
      </div>

      <h2 className="mb-3 font-heading text-lg font-bold">Your vaccine drives</h2>
      {requests.length === 0 ? <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No requests yet.</p> : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4" data-testid={`hp-req-${r.id}`}>
              {editId === r.id ? (
                <DriveForm
                  initial={{ address: r.address, date: r.date, time: r.time, staff_count: r.staff_count, residents_count: r.residents_count, staff_flu: r.staff_flu ?? "", staff_covid: r.staff_covid ?? "", staff_rsv: r.staff_rsv ?? "", residents_flu: r.residents_flu ?? "", residents_covid: r.residents_covid ?? "", residents_rsv: r.residents_rsv ?? "", contacts: r.contacts?.length ? r.contacts : [{ ...EMPTY_CONTACT }], patients: (r.patients || []).map((p) => ({ ...p, insurances: p.insurances?.length ? p.insurances : [{ ...EMPTY_INS }] })) }}
                  submitLabel="Save changes" onSubmit={(payload) => updateDrive(r.id, payload)} onCancel={() => setEditId(null)} />
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2"><span className="font-semibold">{r.date} at {r.time}</span><Badge className={statusColor(r.status)}>{r.status}</Badge></div>
                    <p className="text-sm text-muted-foreground">{r.address}</p>
                    <p className="text-xs text-muted-foreground">Staff: {r.staff_count} (Flu {r.staff_flu || 0} · COVID {r.staff_covid || 0} · RSV {r.staff_rsv || 0}) · Residents: {r.residents_count} (Flu {r.residents_flu || 0} · COVID {r.residents_covid || 0} · RSV {r.residents_rsv || 0}) · Patients on roster: {(r.patients || []).length}</p>
                    {(r.patients || []).length > 0 && (
                      <p className="mt-1 text-xs">
                        <span className="text-primary">Covered: {(r.patients || []).filter((p) => p.insurance_coverage === "covered").length}</span>
                        {" · "}
                        <span className="text-orange-600">Not covered: {(r.patients || []).filter((p) => p.insurance_coverage === "not_covered").length}</span>
                      </p>
                    )}
                    {r.status === "rescheduled" && r.reschedule_note && <p className="mt-1 text-xs text-orange-600">Pharmacy note: {r.reschedule_note}</p>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditId(r.id); setShowNew(false); }} data-testid={`hp-edit-${r.id}`}><Pencil className="mr-1 h-3 w-3" /> Update</Button>
                    {r.status === "confirmed" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportRequest(r)} data-testid={`hp-export-${r.id}`}><Download className="mr-1 h-3 w-3" /> Export</Button>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
